/**
 * Programs (M1 track D4).
 *
 * The list, plus creation in place. A separate "new program" route would be one
 * more screen holding one card holding one form — the toggle keeps staff where
 * the list they just looked at is.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PROGRAM_STATUSES } from '@parinaam-vms/shared';
import type { Program } from '@parinaam-vms/shared';
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  FilterBar,
  LoadingState,
  PageHeader,
  Pager,
  SelectInput,
  StatusBadge,
  TextArea,
  TextInput,
} from '../../components/common';
import { CRUMB } from '../../constants/breadcrumbs';
import { ROWS_PER_PAGE } from '../../constants/pagination';
import { useCreateProgram, usePrograms } from '../../hooks/usePrograms';
import type { ProgramBody } from '../../services/api/programs';
import { countedTitle, formatDate, humaniseCode, orDash } from '../../utils/format';
import styles from './ProgramsPage.module.css';

const STATUS_OPTIONS = PROGRAM_STATUSES.map((status) => ({
  value: status,
  label: humaniseCode(status),
}));

export function ProgramsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);

  const query = usePrograms({
    search: search.trim() || undefined,
    status: status || undefined,
    page,
    page_size: ROWS_PER_PAGE,
  });

  const rows = query.data?.items ?? [];

  return (
    <>
      <PageHeader
        title="Programs"
        breadcrumbs={[CRUMB.programs]}
        actions={
          !creating && (
            <Button variant="primary" onClick={() => setCreating(true)}>
              New program
            </Button>
          )
        }
      />

      {creating && <ProgramForm onClose={() => setCreating(false)} />}

      <Card title={countedTitle('Programs', query.data?.pagination?.total)}>
        <FilterBar label="Filter programs">
          <TextInput
            label="Search"
            placeholder="Name, code or description"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
          />
          <SelectInput
            label="Status"
            placeholder="All Status"
            options={STATUS_OPTIONS}
            value={status}
            onChange={(event) => {
              setStatus(event.target.value);
              setPage(1);
            }}
          />
        </FilterBar>

        {query.isPending ? (
          <LoadingState label="Loading programs…" />
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No programs yet"
            message="Create a programme to start scheduling work."
          />
        ) : (
          <>
            <Pager pagination={query.data?.pagination} onPageChange={setPage} />
            <DataTable<Program>
              caption="Programs"
              rows={rows}
              getRowKey={(row) => row.program_id}
              onRowClick={(row) => navigate(`/programs/${row.program_id}`)}
              columns={[
                { key: 'name', header: 'Name', render: (row) => row.name },
                { key: 'code', header: 'Code', render: (row) => orDash(row.program_code) },
                {
                  key: 'dates',
                  header: 'Runs',
                  render: (row) =>
                    row.start_date || row.end_date
                      ? `${formatDate(row.start_date)} – ${formatDate(row.end_date)}`
                      : '—',
                },
                {
                  key: 'status',
                  header: 'Status',
                  render: (row) => <StatusBadge status={row.status} />,
                },
              ]}
            />
          </>
        )}
      </Card>
    </>
  );
}

function ProgramForm({ onClose }: { onClose: () => void }) {
  const mutation = useCreateProgram();
  const [form, setForm] = useState({
    name: '',
    program_code: '',
    description: '',
    status: 'DRAFT',
    start_date: '',
    end_date: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const set = (field: string) => (value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: '' }));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    /*
     * Everything but the dates is required here (17 Sep 2026).
     *
     * The dates stay optional because a program is often agreed before anybody
     * knows when it runs. That used to carry one exception - an ACTIVE program
     * had to have a start date - which Parinaam removed on 8 Oct 2026, so there
     * is no longer any status that makes a date compulsory.
     */
    const problems: Record<string, string> = {};
    if (form.name.trim() === '') problems.name = 'A program needs a name.';
    if (form.program_code.trim() === '') {
      problems.program_code = 'Give the program a short code.';
    }
    if (form.status === '') problems.status = 'Choose a status.';
    if (form.description.trim() === '') {
      problems.description = 'Say what this program is.';
    }
    // The same rule the API and the CHECK constraint apply (§8.40), said here so
    // staff learn it without a round trip.
    if (form.start_date && form.end_date && form.start_date > form.end_date) {
      problems.end_date = 'The end date cannot be before the start date.';
    }
    if (Object.keys(problems).length > 0) {
      setErrors(problems);
      return;
    }

    const body: ProgramBody = {
      name: form.name.trim(),
      program_code: form.program_code.trim() || null,
      description: form.description.trim() || null,
      status: form.status as ProgramBody['status'],
      start_date: form.start_date || null,
      end_date: form.end_date || null,
    };

    mutation.mutate(body, { onSuccess: onClose });
  };

  return (
    <Card title="New program">
      <form onSubmit={handleSubmit} noValidate>
        {mutation.isError && (
          <p className={styles.error} role="alert">
            {(mutation.error as Error).message}
          </p>
        )}

        <fieldset className={styles.section} disabled={mutation.isPending}>
          <div className={styles.row}>
            <TextInput
              label="Name"
              required
              value={form.name}
              error={errors.name}
              onChange={(event) => set('name')(event.target.value)}
            />
            <TextInput
              label="Short code"
              required
              value={form.program_code}
              error={errors.program_code}
              onChange={(event) => set('program_code')(event.target.value)}
            />
          </div>

          <div className={styles.row}>
            <TextInput
              label="Start date"
              type="date"
              value={form.start_date}
              error={errors.start_date}
              onChange={(event) => set('start_date')(event.target.value)}
            />
            <TextInput
              label="End date"
              type="date"
              value={form.end_date}
              error={errors.end_date}
              onChange={(event) => set('end_date')(event.target.value)}
            />
          </div>

          <SelectInput
            label="Status"
            required
            placeholder="Select…"
            options={STATUS_OPTIONS}
            value={form.status}
            error={errors.status}
            onChange={(event) => set('status')(event.target.value)}
          />

          <TextArea
            label="Description"
            required
            rows={3}
            value={form.description}
            error={errors.description}
            onChange={(event) => set('description')(event.target.value)}
          />
        </fieldset>

        <div className={styles.actions}>
          <Button type="submit" variant="primary" loading={mutation.isPending}>
            Create program
          </Button>
          <Button onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}
